import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("novatus_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Retry idempotent GETs on transient 5xx / network failures. This mainly
// covers k8s ingress cold-starts (502/503/504) and brief network hiccups so
// dashboards (engagement, billing-summary, team analytics) don't render an
// error card on the first load after the pod scales back up.
//
// Rules:
//   - Only retries GET (idempotent). POST/PUT/PATCH/DELETE never retried.
//   - Retries on 502/503/504 or no-response network errors.
//   - 4xx (incl. 401/403/404) never retried — those are deterministic.
//   - Max 2 retries (3 attempts total). Backoff 400ms → 1200ms.
const RETRYABLE_STATUSES = [502, 503, 504];
const MAX_RETRIES = 2;
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (r) => r,
  async (e) => {
    const cfg = e.config;
    const status = e.response?.status;

    // Auth handling first (highest priority).
    if (status === 401) {
      localStorage.removeItem("novatus_token");
      localStorage.removeItem("novatus_user");
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(e);
    }

    // Retry only safe GETs on transient ingress / network failures.
    const isGet = (cfg?.method || "").toLowerCase() === "get";
    const isTransient = (status && RETRYABLE_STATUSES.includes(status)) || !e.response;
    const noRetryFlag = cfg?.headers?.["X-No-Retry"];

    if (cfg && isGet && isTransient && !noRetryFlag) {
      cfg.__retryCount = cfg.__retryCount || 0;
      if (cfg.__retryCount < MAX_RETRIES) {
        cfg.__retryCount += 1;
        const delay = cfg.__retryCount === 1 ? 400 : 1200;
        await _sleep(delay);
        return api.request(cfg);
      }
    }

    return Promise.reject(e);
  }
);

export const LEVELS = ["Anfänger", "Junior Seller", "Senior Seller", "Elite Seller", "Novatus Master"];
export const LEVEL_THRESHOLDS = { "Anfänger": 100, "Junior Seller": 300, "Senior Seller": 700, "Elite Seller": 1500, "Novatus Master": 9999 };

export const DIFFICULTY_COLOR = {
  leicht: "bg-emerald-100 text-emerald-700 border-emerald-200",
  mittel: "bg-amber-100 text-amber-700 border-amber-200",
  schwer: "bg-rose-100 text-rose-700 border-rose-200",
};
